import React from "react";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import LoadSpinner from "../../../utilities/LoadSpinner";
import AddRoleplayToSectionModal from "./AddRoleplayToSectionModal";
import RoleplayPlayerModal from "./RoleplayPlayerModal";
import RoleplayScorecard from "./RoleplayScorecard";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_learning_roleplay_detail,
  API_get_learning_roleplay_attempts,
  API_get_learning_roleplay_attempt,
  API_post_learning_roleplay_attempt_complete,
} from "../../../constants";

export function Roleplay(props) {
  const { block, editor, homeCompany, employeeID, sectionUUID, userType, mode, language } = props;

  // Prefer the drag-proof primitive props; fall back to the legacy object prop
  // (roleplaySelected). BlockNote wipes object props on drag/copy-paste but
  // keeps strings.
  const roleplaySelected = block?.props?.roleplaySelected || {};
  const roleplayUuid = block?.props?.roleplayUuid || roleplaySelected.uuid || null;
  const roleplayName = block?.props?.roleplayName || roleplaySelected.name || "";

  const isEditMode = mode === "edit";
  const isPreview = mode === "preview";

  const [showPicker, setShowPicker] = React.useState(false);

  // Edit/preview: the scenario summary (admin-only detail endpoint).
  const [roleplay, setRoleplay] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  // Complete mode: the learner's attempt history + latest scorecard.
  const [attempts, setAttempts] = React.useState(null);
  // Cover image for the complete-mode card. The learner card fetches attempts,
  // not scenario detail, so the cover rides the attempts response (present even
  // with zero attempts).
  const [coverImage, setCoverImage] = React.useState(null);
  const [playerOpen, setPlayerOpen] = React.useState(false);
  // feedbackOpen drives the panel; expandedScorecard may be null while open
  // (grading unavailable), which renders an explanatory note instead of the
  // scorecard — so the toggle always works.
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [expandedScorecard, setExpandedScorecard] = React.useState(null);
  const [scorecardLoading, setScorecardLoading] = React.useState(false);

  // A "manager reviewing a learner" session is a manager acting on an explicit
  // subject. Self sessions (a learner, or a manager taking their own training)
  // carry no employeeID.
  const isManagerReviewingSubject = userType !== "Employee" && Boolean(employeeID);

  const handleRoleplaySelect = (selected) => {
    if (!selected || !editor || !block) return;
    editor.updateBlock(block, {
      ...block,
      props: {
        ...block.props,
        roleplaySelected: { uuid: selected.uuid, name: selected.name || "" },
        // Drag-proof primitive mirror (object props are wiped on drag).
        roleplayUuid: selected.uuid || "",
        roleplayName: selected.name || "",
      },
    });
  };

  // Load the scenario detail in edit/preview mode so the card shows the
  // description + criteria labels the way the employee will see them.
  React.useEffect(() => {
    if (!(isEditMode || isPreview)) return;
    if (!roleplayUuid) {
      setRoleplay(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setRoleplay(null);
      try {
        const res = await getRightworkAPI(API_get_learning_roleplay_detail, {
          company_id: homeCompany,
          learning_roleplay_uuid: roleplayUuid,
        });
        if (!cancelled) {
          setRoleplay(res.data?.redux?.payload?.learning_roleplay || null);
        }
      } catch (error) {
        // Non-fatal: fall back to the unavailable notice.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isPreview, roleplayUuid, homeCompany]);

  const fetchAttempts = React.useCallback(async () => {
    if (!roleplayUuid || !sectionUUID) return;
    try {
      const res = await getRightworkAPI(API_get_learning_roleplay_attempts, {
        company_id: homeCompany,
        learning_roleplay_uuid: roleplayUuid,
        section_uuid: sectionUUID,
        store_team_member_id: employeeID,
      });
      setAttempts(res.data?.redux?.payload?.attempts || []);
      setCoverImage(res.data?.redux?.payload?.cover_image || null);
    } catch (error) {
      setAttempts([]);
    }
  }, [homeCompany, roleplayUuid, sectionUUID, employeeID]);

  // Load attempt history in complete mode.
  React.useEffect(() => {
    if (mode !== "complete" || isPreview || !roleplayUuid) return;
    fetchAttempts();
  }, [mode, isPreview, roleplayUuid, fetchAttempts]);

  const finishedAttempts = (attempts || []).filter(
    (a) => a.status === "completed" || a.status === "graded"
  );
  const hasCompleted = finishedAttempts.length > 0;
  const latestFinished = finishedAttempts[0] || null;

  const openLatestScorecard = async () => {
    if (!latestFinished || scorecardLoading) return;
    if (feedbackOpen) {
      setFeedbackOpen(false);
      return;
    }
    if (latestFinished.scorecard) {
      setExpandedScorecard(latestFinished.scorecard);
      setFeedbackOpen(true);
      return;
    }
    setScorecardLoading(true);
    try {
      if (!isManagerReviewingSubject && latestFinished.status === "completed") {
        // Grading never ran (or failed) for this attempt — e.g. the learner
        // disconnected right after the conversation ended. attempt_complete
        // is idempotent and retries grading, so this both repairs and loads.
        // (Managers can't call it for a learner's attempt; they get the
        // read-only path below.)
        const res = await postRightworkAPI(API_post_learning_roleplay_attempt_complete, {
          company_id: homeCompany,
          attempt_uuid: latestFinished.uuid,
        });
        setExpandedScorecard(res.data?.redux?.payload?.attempt?.scorecard || null);
        fetchAttempts();
      } else {
        const res = await getRightworkAPI(API_get_learning_roleplay_attempt, {
          company_id: homeCompany,
          attempt_uuid: latestFinished.uuid,
          store_team_member_id: employeeID,
        });
        setExpandedScorecard(res.data?.redux?.payload?.attempt?.scorecard || null);
      }
    } catch (error) {
      setExpandedScorecard(null);
    } finally {
      setScorecardLoading(false);
      // Open even when the scorecard is unavailable — the panel renders an
      // explanatory note, and the button flips to "Hide feedback" so the
      // interaction never dead-ends.
      setFeedbackOpen(true);
    }
  };

  const handlePlayerCompleted = () => {
    fetchAttempts();
    // Close any open feedback panel — it shows the PREVIOUS attempt's
    // scorecard, which is stale the moment a new attempt completes.
    setFeedbackOpen(false);
    setExpandedScorecard(null);
    // Match the section-refresh contract used by the other blocks.
    if (props.setRefreshSection) props.setRefreshSection(true);
  };

  // Criteria labels for the "what you'll practice" list on the card.
  const criteriaLabels = (roleplay?.criteria || []).map((c) => c.label).filter(Boolean);

  const headerCard = (children, tone, coverUrl) => (
    <div
      className={`rw-form-block my-3 w-full overflow-hidden rounded-lg border ${
        tone === "completed"
          ? "border-emerald-200 bg-emerald-50"
          : "border-slate-200 bg-white"
      }`}
      contentEditable={false}
    >
      {coverUrl && (
        <div className="aspect-[3/1] w-full overflow-hidden bg-slate-100">
          <img src={coverUrl} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="px-4 py-4">
        <div className="flex items-start gap-3">
          {!coverUrl && (
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                tone === "completed" ? "bg-emerald-100" : "bg-violet-100"
              }`}
            >
              {tone === "completed" ? (
                <CheckCircleIcon className="text-emerald-600" style={{ fontSize: 20 }} />
              ) : (
                <ConnectWithoutContactIcon className="text-violet-600" style={{ fontSize: 20 }} />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">{children}</div>
        </div>
      </div>
    </div>
  );

  // ---------- EDIT MODE (authoring) ----------
  if (isEditMode) {
    if (!roleplayUuid) {
      return (
        <>
          <button
            type="button"
            contentEditable={false}
            onClick={() => setShowPicker(true)}
            className="my-3 w-full p-4 border-2 border-dashed border-violet-300 rounded-xl text-sm text-violet-700 hover:bg-violet-50 hover:border-violet-500 transition-colors flex items-center justify-center gap-2"
          >
            <ConnectWithoutContactIcon style={{ fontSize: 18 }} />
            <span>Select a practice scenario</span>
          </button>
          <AddRoleplayToSectionModal
            open={showPicker}
            handleClose={() => setShowPicker(false)}
            handleRoleplaySelect={handleRoleplaySelect}
            homeCompany={homeCompany}
          />
        </>
      );
    }
  }

  // ---------- EDIT + PREVIEW card ----------
  if (isEditMode || isPreview) {
    return headerCard(
      <>
        <div className="flex items-start justify-between gap-2">
          <span className="font-semibold text-slate-900 text-base">
            {roleplay?.name || roleplayName || "Practice Scenario"}
          </span>
          {isEditMode && props.onEditAsset && roleplayUuid && (
            <button
              type="button"
              onClick={() => props.onEditAsset(roleplayUuid)}
              className="text-xs font-medium text-violet-700 hover:text-violet-800 shrink-0"
            >
              Edit
            </button>
          )}
        </div>
        {isLoading ? (
          <div className="flex py-3">
            <LoadSpinner />
          </div>
        ) : roleplay ? (
          <>
            {roleplay.description && (
              <p className="text-slate-600 text-sm mt-1">{roleplay.description}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                AI conversation practice
              </span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                Up to {roleplay.max_turns} replies
              </span>
            </div>
            {criteriaLabels.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Coached on
                </div>
                <ul className="text-sm text-slate-700 space-y-0.5">
                  {criteriaLabels.map((label, i) => (
                    <li key={i}>• {label}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-400 mt-1">
            This practice scenario is currently unavailable.
          </p>
        )}
      </>,
      undefined,
      roleplay?.cover?.url || null,
    );
  }

  // ---------- COMPLETE MODE (take training) ----------
  if (!roleplayUuid) {
    return (
      <div className="my-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400">
        Practice scenario not configured
      </div>
    );
  }

  return (
    <>
      {headerCard(
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-base">
              {roleplayName || "Practice Scenario"}
            </span>
            {hasCompleted && (
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 font-medium rounded text-xs">
                Completed
              </span>
            )}
            {finishedAttempts.length > 1 && (
              <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-medium rounded text-xs">
                {finishedAttempts.length} attempts
              </span>
            )}
          </div>
          <p className="text-slate-600 text-sm mt-1">
            Practice this conversation with an AI character. You'll get coaching
            feedback at the end. Finishing the conversation is what counts.
          </p>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {isManagerReviewingSubject ? (
              // A manager reviewing a learner doesn't practice on their behalf —
              // conversations are self-only. They see status + the scorecard.
              attempts === null ? (
                <LoadSpinner />
              ) : hasCompleted ? (
                <button
                  type="button"
                  onClick={openLatestScorecard}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-violet-700 border border-violet-200 hover:bg-violet-50"
                >
                  {scorecardLoading
                    ? "Loading…"
                    : feedbackOpen
                    ? "Hide feedback"
                    : "View feedback"}
                </button>
              ) : (
                <span className="text-sm text-slate-500">
                  Not attempted yet.
                </span>
              )
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setPlayerOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700"
                >
                  {hasCompleted ? (
                    <ReplayIcon style={{ fontSize: 18 }} />
                  ) : (
                    <PlayArrowIcon style={{ fontSize: 18 }} />
                  )}
                  {hasCompleted ? "Practice again" : "Start practice"}
                </button>
                {hasCompleted && (
                  <button
                    type="button"
                    onClick={openLatestScorecard}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-violet-700 border border-violet-200 hover:bg-violet-50"
                  >
                    {scorecardLoading
                      ? "Loading…"
                      : feedbackOpen
                      ? "Hide feedback"
                      : "View feedback"}
                  </button>
                )}
              </>
            )}
          </div>

          {feedbackOpen && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              {expandedScorecard ? (
                <RoleplayScorecard scorecard={expandedScorecard} />
              ) : (
                <p className="text-sm text-slate-500">
                  Feedback isn't available for the latest attempt. Try again in
                  a moment.
                </p>
              )}
            </div>
          )}
        </>,
        hasCompleted ? "completed" : "default",
        coverImage,
      )}

      {playerOpen && (
        <RoleplayPlayerModal
          open={playerOpen}
          onClose={() => setPlayerOpen(false)}
          homeCompany={homeCompany}
          roleplayUuid={roleplayUuid}
          roleplayName={roleplayName}
          sectionUUID={sectionUUID}
          language={language}
          onCompleted={handlePlayerCompleted}
        />
      )}
    </>
  );
}

export default Roleplay;
